import type {
  FirewallReplayAppliedContext,
  FirewallReplayDiff,
  FirewallReplaySampleResult,
  FirewallReplaySummary,
  FirewallReplayVerdict,
  ReplayFirewallRulesetResponse,
} from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import { formatLocalTimestamp } from './formatters.ts';
import { Chip, Table, TBody, Td, Th, THead, Tr } from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * One replay report, rendered per sample (#1760, report from #1759).
 *
 * Three things this deliberately does NOT do:
 *
 *   - It does not render `zero_difference` as a verdict. The gate does not read
 *     that field; a green "no differences" badge next to an activate button
 *     would read as "cleared to arm", which it never means.
 *   - It does not treat `corpus_fingerprint` as an identity. It answers "was
 *     this the same batch of samples", not "was this the same batch of
 *     meanings" — two corpora that exercise the same rules have different
 *     fingerprints, and the same corpus replayed twice has the same one.
 *   - It does not show a matched rule's `description`. The response omits it on
 *     purpose: a description is the one field that restates the criterion in
 *     readable form, and readability is evadability.
 *
 * `baseline_unresolvable` is a legal state, not an error. It means the armed
 * chain no longer resolves — the ruleset is fail-open at this moment — so the
 * report covers the candidate alone. Blocking on it would block the fix.
 */

const HASH_PREVIEW_LENGTH = 12;

const DIFF_TONE = {
  unchanged: 'neutral',
  'pass-to-intercept': 'bad',
  'intercept-to-pass': 'warn',
  'action-changed': 'warn',
  'rule-changed': 'signal',
  errored: 'bad',
} as const satisfies Record<FirewallReplayDiff, ChipTone>;

const DIFF_KEY = {
  unchanged: 'unchanged',
  'pass-to-intercept': 'pass_to_intercept',
  'intercept-to-pass': 'intercept_to_pass',
  'action-changed': 'action_changed',
  'rule-changed': 'rule_changed',
  errored: 'errored',
} as const satisfies Record<FirewallReplayDiff, string>;

const COUNT_KEYS = [
  'pass_to_intercept',
  'intercept_to_pass',
  'action_changed',
  'rule_changed',
  'unchanged',
  'errored',
] as const;

function countOf(summary: FirewallReplaySummary, key: (typeof COUNT_KEYS)[number]): number {
  switch (key) {
    case 'pass_to_intercept':
      return summary.pass_to_intercept;
    case 'intercept_to_pass':
      return summary.intercept_to_pass;
    case 'action_changed':
      return summary.action_changed;
    case 'rule_changed':
      return summary.rule_changed;
    case 'unchanged':
      return summary.unchanged;
    default:
      return summary.errored;
  }
}

function Verdict({ verdict }: { readonly verdict: FirewallReplayVerdict | null }): JSX.Element {
  const t = useT();
  if (verdict === null) {
    return <span className="text-label text-paper-low">{t('firewall_rulesets.replay.verdict.none')}</span>;
  }
  const detail = verdict.deny_as ?? verdict.synthesize_as ?? verdict.error_class;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-label text-paper-mid">
        {t(`firewall_rulesets.replay.outcome.${verdict.outcome}`)}
        {verdict.action === null ? '' : ` · ${verdict.action}`}
        {detail === null ? '' : ` · ${detail}`}
      </span>
      <span className="font-mono text-micro text-paper-low">
        {verdict.rule_id ?? t('firewall_rulesets.replay.no_rule')}
      </span>
    </span>
  );
}

/**
 * `applied_context` is what `$.derived.*` actually held while the sample was
 * judged — NOT what the operator typed into the corpus. The two diverge
 * whenever a submitted key was outside the four-key whitelist, or absent, and
 * a rule that reads `$.derived.lane` then judged the sample under `null`
 * rather than under the lane the operator believed they were testing. Showing
 * only the submitted value would make that divergence invisible, which turns a
 * report about the wrong thing into a report that looks right.
 */
function appliedContextText(context: FirewallReplayAppliedContext, absent: string): string {
  const parts: readonly [string, string | boolean | null][] = [
    ['client_format', context.client_format],
    ['lane', context.lane],
    ['stream', context.stream],
    ['endpoint', context.endpoint],
  ];
  return parts
    .map(([key, value]) => `${key}=${value === null ? absent : String(value)}`)
    .join(' · ');
}

function SampleRow({ sample }: { readonly sample: FirewallReplaySampleResult }): JSX.Element {
  const t = useT();
  return (
    <Tr interactive={false}>
      <Td className="font-mono text-label text-paper">
        <span className="flex flex-col gap-0.5">
          <span className="whitespace-nowrap">#{sample.index + 1}</span>
          <span
            className="text-micro text-paper-low"
            data-testid={`firewall-replay-applied-context-${String(sample.index)}`}
          >
            {appliedContextText(
              sample.applied_context,
              t('firewall_rulesets.replay.context_absent'),
            )}
          </span>
        </span>
      </Td>
      <Td>
        {sample.diff === null ? (
          <span className="text-label text-paper-low">
            {t('firewall_rulesets.replay.verdict.none')}
          </span>
        ) : (
          <Chip tone={DIFF_TONE[sample.diff]} lamp={false}>
            {t(`firewall_rulesets.replay.diff.${DIFF_KEY[sample.diff]}`)}
          </Chip>
        )}
      </Td>
      <Td><Verdict verdict={sample.candidate} /></Td>
      <Td><Verdict verdict={sample.baseline} /></Td>
    </Tr>
  );
}

export function FirewallReplayReport({
  report,
}: {
  readonly report: ReplayFirewallRulesetResponse;
}): JSX.Element {
  const t = useT();
  const { record } = report;
  const { summary } = record;
  const zeroKey = summary.zero_difference === null
    ? 'zero_unknown'
    : summary.zero_difference ? 'zero_yes' : 'zero_no';

  return (
    <div className="flex flex-col gap-3" data-testid="firewall-replay-report">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-label text-paper-mid">
        <span className="font-mono text-micro text-paper-low" data-testid="firewall-replay-record-id">
          {record.id}
        </span>
        <span>{t('firewall_rulesets.replay.record.version', { version: record.version })}</span>
        <span>
          {record.baseline_version === null
            ? t('firewall_rulesets.replay.record.no_baseline')
            : t('firewall_rulesets.replay.record.baseline', { version: record.baseline_version })}
        </span>
        <span>{t('firewall_rulesets.replay.record.samples', { count: record.sample_count })}</span>
        <span className="font-mono text-micro text-paper-low">
          {formatLocalTimestamp(record.created_at, { seconds: true })}
        </span>
      </div>

      {summary.baseline_unresolvable ? (
        <p
          className="m-0 rounded-lg bg-well p-3 text-label text-warn"
          data-testid="firewall-replay-baseline-unresolvable"
        >
          {t('firewall_rulesets.replay.unresolvable')}
        </p>
      ) : !summary.compared ? (
        <p
          className="m-0 rounded-lg bg-well p-3 text-label text-paper-mid"
          data-testid="firewall-replay-not-compared"
        >
          {t('firewall_rulesets.replay.not_compared')}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2" data-testid="firewall-replay-counts">
        {COUNT_KEYS.map((key) => (
          <span key={key} className="rounded bg-well px-2 py-1 text-micro text-paper-mid">
            {t(`firewall_rulesets.replay.diff.${key}`)}
            <span className="ml-1.5 font-mono text-paper">{countOf(summary, key)}</span>
          </span>
        ))}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-label text-paper-mid" data-testid="firewall-replay-zero-difference">
          {t('firewall_rulesets.replay.zero_difference')}
          <span className="ml-1.5 font-mono text-paper">
            {t(`firewall_rulesets.replay.${zeroKey}`)}
          </span>
        </span>
        <span className="text-micro text-paper-low">{t('firewall_rulesets.replay.zero_note')}</span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-label text-paper-mid" data-testid="firewall-replay-fingerprint">
          {t('firewall_rulesets.replay.fingerprint')}
          <span className="ml-1.5 font-mono text-micro text-paper" title={record.corpus_fingerprint}>
            {record.corpus_fingerprint.slice(0, HASH_PREVIEW_LENGTH)}
          </span>
        </span>
        <span className="text-micro text-paper-low">
          {t('firewall_rulesets.replay.fingerprint_note')}
        </span>
      </div>

      <Table className="min-w-0" testId="firewall-replay-samples">
        <THead>
          <Tr interactive={false}>
            <Th>{t('firewall_rulesets.replay.col.sample')}</Th>
            <Th>{t('firewall_rulesets.replay.col.diff')}</Th>
            <Th>{t('firewall_rulesets.replay.col.candidate')}</Th>
            <Th>{t('firewall_rulesets.replay.col.baseline')}</Th>
          </Tr>
        </THead>
        <TBody>
          {report.samples.map((sample) => (
            <SampleRow key={sample.index} sample={sample} />
          ))}
        </TBody>
      </Table>
      <p className="m-0 text-micro text-paper-low" data-testid="firewall-replay-context-note">
        {t('firewall_rulesets.replay.applied_context_note')}
      </p>
      <p className="m-0 text-micro text-paper-low">
        {t('firewall_rulesets.replay.description_absent')}
      </p>
    </div>
  );
}
